// Dual-plane auth seam — every auth touchpoint (session state, sign-in/out,
// bearer tokens, custom claims) goes through here so pages never care which
// plane is active.
//
// firebase plane (default): thin pass-throughs over the existing Firebase auth
//   — behavior is identical to the pre-seam code. onAuthChanged passes the RAW
//   firebase user (a superset of the normalized shape: uid, email,
//   displayName, isAnonymous, …) so consumers like App.js's
//   `user.isAnonymous` check keep working unchanged.
// supabase plane: GoTrue. Users normalize to { uid, email, displayName,
//   isAnonymous }; the bearer token is the session access_token (the backend
//   accepts it everywhere it accepts a Firebase ID token); claims come from
//   session.user.app_metadata { internal, appRole, allowed }.
import firebase from '../firebaseConfig';
import { isPg, getSupabase } from './supabaseClient';
import { kitEnv } from './kitEnv';

const EMPTY_CLAIMS = { internal: false, appRole: null, allowed: false, email: null };

function normSupabaseUser(u) {
  if (!u) return null;
  return {
    uid: u.id,
    email: u.email || '',
    displayName: u.user_metadata?.full_name || u.user_metadata?.name || '',
    isAnonymous: !!u.is_anonymous,
  };
}

// Subscribe to auth state. cb(user|null); returns an unsubscribe fn. Fires
// immediately with the current state on both planes (supabase via the
// INITIAL_SESSION event).
export function onAuthChanged(cb) {
  if (isPg()) {
    const supabase = getSupabase();
    if (!supabase) {
      cb(null);
      return () => {};
    }
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      cb(normSupabaseUser(session?.user || null));
    });
    return () => data.subscription.unsubscribe();
  }
  if (!firebase.apps.length) {
    cb(null);
    return () => {};
  }
  return firebase.auth().onAuthStateChanged(cb);
}

export async function signIn(email, password) {
  if (isPg()) {
    const { data, error } = await getSupabase().auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }
  return firebase.auth().signInWithEmailAndPassword(email, password);
}

export async function signUp(email, password) {
  if (isPg()) {
    const { data, error } = await getSupabase().auth.signUp({ email, password });
    if (error) throw error;
    return data;
  }
  return firebase.auth().createUserWithEmailAndPassword(email, password);
}

export async function signInWithGoogle() {
  if (isPg()) {
    const { data, error } = await getSupabase().auth.signInWithOAuth({ provider: 'google' });
    if (error) throw error;
    return data;
  }
  return firebase.auth().signInWithPopup(new firebase.auth.GoogleAuthProvider());
}

export async function signOutUser() {
  if (isPg()) {
    const { error } = await getSupabase().auth.signOut();
    if (error) throw error;
    return undefined;
  }
  return firebase.auth().signOut();
}

// Email password reset. firebase plane: Firebase mails the link and hosts
// the reset page. supabase plane: the kit backend mints GoTrue's recovery
// link and mails it (SMTP or the control-plane relay); the link lands on
// /login?reset=1 with a recovery session and completePasswordReset() sets
// the new password. Always resolves — never says whether the email exists.
export async function sendPasswordReset(email) {
  if (isPg()) {
    const base = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '').replace(/\/$/, '');
    const res = await fetch(`${base}/auth/reset-request`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
    if (res.status === 429) throw new Error('Too many reset requests — try again in a few minutes.');
    if (!res.ok) throw new Error('Could not start the reset. Try again.');
    return undefined;
  }
  return firebase.auth().sendPasswordResetEmail(email, { url: `${window.location.origin}/login` });
}

// supabase plane only: fires cb() when a recovery link opens the app
// (supabase-js turns the URL fragment into a session and emits
// PASSWORD_RECOVERY). Returns an unsubscribe fn.
export function onPasswordRecovery(cb) {
  if (!isPg()) return () => {};
  const supabase = getSupabase();
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((event) => { if (event === 'PASSWORD_RECOVERY') cb(); });
  return () => data.subscription.unsubscribe();
}

export async function completePasswordReset(newPassword) {
  if (!isPg()) throw new Error('not supported on this plane');
  const { error } = await getSupabase().auth.updateUser({ password: newPassword });
  if (error) throw error;
  return undefined;
}

// Change the signed-in user's password. Verifies the current password first
// (re-auth) on both planes — an unlocked screen must not be enough. Operators
// start from the seeded temp password and rotate it here (or use the email
// reset above).
export async function changePassword(currentPassword, newPassword) {
  if (isPg()) {
    const supabase = getSupabase();
    const { data } = await supabase.auth.getSession();
    const email = data?.session?.user?.email;
    if (!email) throw new Error('not signed in');
    const { error: reauthErr } = await supabase.auth.signInWithPassword({ email, password: currentPassword });
    if (reauthErr) throw new Error('current password is incorrect');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return undefined;
  }
  const user = firebase.auth().currentUser;
  if (!user?.email) throw new Error('not signed in');
  const cred = firebase.auth.EmailAuthProvider.credential(user.email, currentPassword);
  await user.reauthenticateWithCredential(cred).catch(() => {
    throw new Error('current password is incorrect');
  });
  return user.updatePassword(newPassword);
}

// Bearer token for backend calls: Firebase ID token | Supabase access_token.
// null when signed out (callers already handle a missing Authorization header).
export async function getIdToken() {
  if (isPg()) {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  }
  const user = firebase.auth().currentUser;
  return user ? user.getIdToken() : null;
}

// Normalized custom claims { internal, appRole, allowed, email } — Firebase ID
// token claims | Supabase app_metadata. All-falsy when signed out.
export async function getClaims() {
  if (isPg()) {
    const supabase = getSupabase();
    if (!supabase) return { ...EMPTY_CLAIMS };
    const { data } = await supabase.auth.getSession();
    const user = data?.session?.user;
    if (!user) return { ...EMPTY_CLAIMS };
    const meta = user.app_metadata || {};
    return {
      internal: meta.internal === true,
      appRole: meta.appRole || null,
      allowed: meta.allowed === true,
      email: user.email || null,
    };
  }
  const user = firebase.auth().currentUser;
  if (!user) return { ...EMPTY_CLAIMS };
  const res = await user.getIdTokenResult();
  return {
    internal: res.claims.internal === true,
    appRole: res.claims.appRole || null,
    allowed: res.claims.allowed === true,
    email: res.claims.email || user.email || null,
  };
}

// Current signed-in user (raw firebase user | normalized supabase user) —
// used by write helpers that stamp email/displayName into docs.
export async function getCurrentUser() {
  if (isPg()) {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return normSupabaseUser(data?.session?.user || null);
  }
  return firebase.auth().currentUser;
}
